import React, { useMemo, useCallback } from "react";
import { Button, Icon } from "semantic-ui-react";

import moment from "moment";
import { useIntl } from "react-intl";

// Common
import DateTextBox from "react-lib/apps/common/DateTextBox";

// UX
import DPOHistoryReviewVisit from "./DPOHistoryReviewVisit";
import ModalItemFilterCustom from "./ModalItemFilterCustom";

// Utils
import * as utils from "react-lib/utils";
import { formatUTCtoBEString } from "react-lib/utils/dateUtils";

// Report / Consent ยังไม่เปิดใช้
const HISTORY_REPORT_ENABLED = false;
const HISTORY_CONSENT_ENABLED = false;

// Types
export type ReviewVisitProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: Function) => any;

  // data
  patientFamilyTPList?: any[];
  columnsHistoryVisit?: any[];
  historyReviewVisitData: any;
  patientId: any;
  tableHeight?: string;
  languageUX: string;
  historyDate: any;
  // filter
  doctorFilterList?: any[];
  clinicFilterList?: any[];
  openModalFilter?: boolean;
  openModalFilterClinic?: boolean;
  // callback
  handleGetHistoryVisit: () => any;
  handleOnFocusFindingsRowVisit: (state: any, rowInfo: any) => any;
  onChangeDate?: (key: "start" | "end") => (date: string) => any;
};

const ReviewVisit = (props: ReviewVisitProps) => {
  const intl = useIntl();

  // Callback
  const checkHistoryFilterActive = useCallback((list: any[] = []) => {
    return list.find((item: any) => item.is_selected === false);
  }, []);

  const handlePreGenReport = useCallback(
    (row: any) => () => {
      if (row.original?.encounter) {
        props.onEvent({
          message: "handlePreGenReport",
          params: { encounter: row.original?.encounter, emr_id: row.original?.emr, started: row.original?.started },
        });
      } else {
        alert(" Not encounter from row");
      }
    },
    []
  );

  const handleOpenModGallery = useCallback(
    (original: any) => () => {
      props.onEvent({ message: "handleOpenModGallery", params: { data: original } });
    },
    []
  );
  const handlePrintPatientFamilyTP = useCallback(
    (data: any) => () => {
      props.onEvent({ message: "HandlePrintPatientFamilyTP", params: { ...data, intl } });
    },
    []
  );

  const renderDateApproveCell = useCallback(({ original }: any = {}) => {
    return <DateApproveCell original={original} />;
  }, []);

  const renderDoctorCell = useCallback(({ original }: any = {}) => {
    let doctorName = ""
    if (original.student_name) {
      doctorName = original.status === "CHECKED_OUT"
            ? `${original.approve_by_name} /\n${original.student_name}`
            : `${original.doctor_fullname} /\n${original.student_name}`
    } else {
      doctorName = original.status === "CHECKED_OUT" ? `${original.approve_by_name}` : `${original.doctor_fullname}`;
    }

    let btnColor = ""
    let btnLabel = ""
    if (original.approve_status === "DRAFT") {
      btnColor = "orange";
      btnLabel = "Draft";
    } else if (original.approve_status === "FINISH_EDITING") {
      btnColor = "blue";
      btnLabel = "Finish Editing";
    }
    return (
      <div>
        {doctorName}
        {btnColor !== "" && btnLabel !== "" && (
          <Button
            color={btnColor}
            basic
            style={{
              borderRadius: "80px",
              padding: "5px 10px",
              marginLeft: "10px",
              float: "right",
            }}
          >
            {btnLabel}
          </Button>
        )}
      </div>
    );
  }, []);

  const renderGalleryCell = useCallback(({ original }: any = {}) => {
    return (
      <div style={{ textAlign: "center" }}>
        {original.pictures?.length && <Icon name="image outline" style={{ cursor: "pointer", marginTop: -10 }} onClick={handleOpenModGallery(original)} />}
      </div>
    );
  }, []);

  const renderReportCell = useCallback((row: any) => {
    return <div style={{ textAlign: "center" }}>{<Icon name="th" style={{ cursor: "pointer", marginTop: -10 }} onClick={handlePreGenReport(row)} />}</div>;
  }, []);

  const renderCheckoutCell = useCallback(({ original }: any = {}) => {
    const endTime: any[] = formatUTCtoBEString(original.encounter_checkout_time);

    return <div style={{ textAlign: "center" }}>{original.encounter_checkout_time ? `${endTime[0]}  [${endTime[1]}]` : ""}</div>;
  }, []);

  const renderPeriodCell = useCallback(({ original }: any = {}) => {
    const enStart: moment.Moment = moment(original.encounter_checkin_time, "YYYY-MM-DD HH:mm");
    const enEnd: moment.Moment = moment(original.encounter_checkout_time, "YYYY-MM-DD HH:mm");
    const diffDate = enEnd.diff(enStart);
    const duration = moment.duration(diffDate);
    const dday = duration.days();
    const dhour = duration.hours();
    const dminute = duration.minutes();

    return (
      <div style={{ textAlign: "center" }}>
        {dday ? `${dday} day` : ""}
        {dhour ? ` ${dhour} hr` : ""}
        {dminute ? ` ${dminute} min` : ""}
      </div>
    );
  }, []);

  const renderConsentCell = useCallback(
    ({ original }: any = {}) => {
      const patientFamilyTPData = props.patientFamilyTPList?.find((item: any) => item.encounter === original.encounter);

      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {!!patientFamilyTPData && (
            <img
              aria-hidden="true"
              src="/static/images/consentFile.png"
              alt="consent file"
              style={{ width: "25px" }}
              onClick={handlePrintPatientFamilyTP(patientFamilyTPData)}
            />
          )}
        </div>
      );
    },
    [props.patientFamilyTPList]
  );

  // Memo
  const columnsHistoryVisit = useMemo(() => {
    const columns = [
      {
        Header: "Date",
        resizeable: false,
        accessor: "date",
        Cell: renderDateApproveCell,
      },
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {"Dentist(Instructor)/Student"}
            <Icon
              className="review-visit-filter-doctor"
              name="filter"
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                zIndex: 2,
                color: checkHistoryFilterActive(props.doctorFilterList) ? "yellow" : "",
              }}
              onClick={() => props.setProp("openModalFilter", !props.openModalFilter)}
            />
          </div>
        ),
        accessor: "doctor_fullname",
        resizeable: false,
        minWidth: 170,
        Cell: renderDoctorCell,
      },
      {
        Header: (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            {intl.formatMessage({ id: "cukey54" })}
            <Icon
              className="review-visit-filter-clinic"
              name="filter"
              style={{
                marginLeft: "5px",
                cursor: "pointer",
                zIndex: 2,
                color: checkHistoryFilterActive(props.clinicFilterList) ? "yellow" : "",
              }}
              onClick={() => props.setProp("openModalFilterClinic", !props.openModalFilterClinic)}
            />
          </div>
        ),
        accessor: "division_name",
        resizeable: false,
        // Cell: (row: any) => <div style={{ textAlign: "center" }}>{row.original.items[0].doctor_fullname}</div>,
      },
      {
        Header: "Chief complaints",
        accessor: "chief_complaints",
        resizeable: false,
      },
      {
        Header: intl.formatMessage({ id: "cukey169" }),
        accessor: "present_illness",
        resizeable: false,
      },
      {
        Header: intl.formatMessage({ id: "cukey35" }),
        accessor: "additional_comment",
        resizeable: false,
      },
      {
        Header: "Gallery",
        accessor: "gallery",
        resizeable: false,
        width: 70,
        Cell: renderGalleryCell,
      },
      ...(HISTORY_REPORT_ENABLED
        ? [
            {
              Header: "Report",
              accessor: "report",
              resizeable: false,
              Cell: renderReportCell,
            },
          ]
        : []),
      {
        Header: "Checkout",
        resizeable: false,
        accessor: "checkout",
        Cell: renderCheckoutCell,
      },
      {
        Header: "Period",
        resizeable: false,
        accessor: "period",
        Cell: renderPeriodCell,
      },
      ...(HISTORY_CONSENT_ENABLED
        ? [
            {
              Header: "Consent",
              resizeable: false,
              accessor: "consent",
              Cell: renderConsentCell,
            },
          ]
        : []),
      ...(props.columnsHistoryVisit || []),
    ];

    return Array.from(new Map(columns.map((item) => [item.accessor, item])).values());
  }, [
    props.columnsHistoryVisit,
    props.clinicFilterList,
    props.openModalFilterClinic,
    props.doctorFilterList,
    props.openModalFilter,
    props.patientFamilyTPList,
  ]);

  // Handler
  const handleDoctorFilter = (list: any, filterArray: string[]) => {
    props.onEvent({ message: "SetDoctorFilterVisit", params: { filter: list } });
  };

  const handleClinicFilter = (list: any, filterArray: string[]) => {
    props.onEvent({ message: "SetClinicFilter", params: { filter: list } });
  };

  return (
    <DPOHistoryReviewVisit
      columnsHistoryVisit={columnsHistoryVisit}
      cardFilterDentistElement={
        props.openModalFilter ? (
          <ModalItemFilterCustom
            name="Dentist/Instructor"
            style={{ margin: "0.35rem 0 0 -7rem" }}
            data={props.doctorFilterList}
            onClose={() => props.setProp("openModalFilter", false)}
            textField="name"
            target="review-visit-filter-doctor"
            classNameZoom={["#main-ptm #detail", "#main-dpo .history-tab"]}
            onSelect={handleDoctorFilter}
          />
        ) : null
      }
      cardFilterClinicElement={
        props.openModalFilterClinic ? (
          <ModalItemFilterCustom
            name="Clinic"
            style={{ margin: "0.35rem 0 0 -7rem" }}
            data={props.clinicFilterList}
            onClose={() => props.setProp("openModalFilterClinic", false)}
            textField="name"
            target="review-visit-filter-clinic"
            classNameZoom={["#main-ptm #detail", "#main-dpo .history-tab"]}
            onSelect={handleClinicFilter}
          />
        ) : null
      }
      historyReviewVisitData={props.historyReviewVisitData}
      handleGetHistoryVisit={props.handleGetHistoryVisit}
      handleOnFocusFindingsRowVisit={props.handleOnFocusFindingsRowVisit}
      patientId={props.patientId}
      tableHeight={props.tableHeight}
      languageUX={props.languageUX}
      DateStart={
        <DateTextBox
          name="dtbStartDate"
          maxDate={utils.adToBe(moment())}
          value={props.historyDate.visitStartDate}
          // onChange={(date) => {
          //   props.setProp("historyDate.visitStartDate", date);
          // }}
          onChange={props.onChangeDate?.("start")}
        />
      }
      DateTo={
        <DateTextBox
          name="dtbEndDate"
          maxDate={utils.adToBe(moment())}
          value={props.historyDate.visitEndDate}
          // onChange={(date) => {
          //   props.setProp("historyDate.visitEndDate", date);
          // }}
          onChange={props.onChangeDate?.("end")}
        />
      }
    />
  );
};

/* ------------------------------------------------------ */

/*                    DateApproveCell;                    */

/* ------------------------------------------------------ */
const DateApproveCell = (props: any) => {
  const intl = useIntl();

  const startTime: any[] = formatUTCtoBEString(props.original.encounter_checkin_time);

  // 69711
  let isApproved = false;
  if ( props.original?.student) {
    // student encounter
    if (["APPROVED"].includes(props.original?.approve_status) || (["BLANK"].includes(props.original?.approve_status) && ["DISCHARGED", "CHECKED_OUT"].includes(props.original?.status))) {
      isApproved = true
    }
  } else {
    // teacher encounter / doctor encounter
    if (["DISCHARGED", "CHECKED_OUT"].includes(props.original?.status)) {
      isApproved = true
    }
  }

  return (
    <div>
      <div style={{ textAlign: "center" }}>
        {props.original.started}
        {startTime ? `  [${startTime[1]}]` : ""}
      </div>
      <div style={{ textAlign: "center", display: props.original?.is_draft === undefined ? "none" : "" }}>
        {props.original?.encounter_type === "OTH" ? intl.formatMessage({ id: "cukey504" }) : intl.formatMessage({ id: "cukey503" })}
        { isApproved ? (
          // "(อนุมัติแล้ว)"
          <Button
            color="green"
            basic
            style={{
              borderRadius: "80px",
              padding: "5px 10px",
              marginLeft: "10px",
            }}
          >
            {intl.formatMessage({ id: "cukey505" })}
          </Button>
        ) : (
          // "(รออนุมัติ)"
          <Button
            color="violet"
            basic
            style={{
              borderRadius: "80px",
              padding: "5px 10px",
              marginLeft: "10px",
            }}
          >
            {intl.formatMessage({ id: "cukey506" })}
          </Button>
        )}
      </div>
    </div>
  );
};

ReviewVisit.displayName = "ReviewVisit";

export default React.memo(ReviewVisit);
